import {
    ConstDefinitionData,
    CoreCompletionItem,
    CoreCompletionItemInsertTextRule,
    CoreCompletionItemKind,
    CoreCompletionList,
    ElementSchema,
    LVGLSchemaService,
    TRIGGER_CHARACTERS,
    XML_LANGUAGE_ID,
    provideCompletionItems,
    provideHover
} from '@lved/shared-core';
import { FrontendApplicationContribution } from '@theia/core/lib/browser';
import { DisposableCollection } from '@theia/core/lib/common/disposable';
import { inject, injectable } from '@theia/core/shared/inversify';
import * as monaco from '@theia/monaco-editor-core';
import { XmlSchemaContribution } from './xml-schema-contribution';

@injectable()
export class MonacoXmlFeaturesContribution implements FrontendApplicationContribution {
    @inject(XmlSchemaContribution)
    protected readonly xmlSchemaContribution!: XmlSchemaContribution;

    protected completionProvider: monaco.IDisposable | undefined;
    protected hoverProvider: monaco.IDisposable | undefined;
    protected colorProvider: monaco.IDisposable | undefined;
    protected definitionProvider: monaco.IDisposable | undefined;
    protected readonly toDispose: DisposableCollection = new DisposableCollection();

    async onStart(): Promise<void> {
        this.toDispose.push(
            this.xmlSchemaContribution.onDidChangeSchemaService((schemaService: LVGLSchemaService) => {
                this.registerProviders(schemaService);
            })
        );

        const schemaService: LVGLSchemaService | undefined = await this.xmlSchemaContribution.ensureInitialized();
        if (!schemaService) return;
        this.registerProviders(schemaService);
    }

    onStop(): void {
        this.toDispose.dispose();
    }

    protected registerProviders(schemaService: LVGLSchemaService): void {
        if (!this.completionProvider) this.registerCompletionProvider(schemaService);
        if (!this.hoverProvider) this.registerHoverProvider(schemaService);
        if (!this.colorProvider) this.registerColorProvider(schemaService);
        if (!this.definitionProvider) this.registerDefinitionProvider(schemaService);
    }

    protected registerCompletionProvider(schemaService: LVGLSchemaService): void {
        this.completionProvider = monaco.languages.registerCompletionItemProvider(XML_LANGUAGE_ID, {
            triggerCharacters: TRIGGER_CHARACTERS,
            provideCompletionItems: (model: monaco.editor.ITextModel, position: monaco.Position) =>
                this.transformCompletions(provideCompletionItems(schemaService, model, position))
        });
        this.toDispose.push(this.completionProvider);
    }

    protected registerHoverProvider(schemaService: LVGLSchemaService): void {
        this.hoverProvider = monaco.languages.registerHoverProvider(XML_LANGUAGE_ID, {
            provideHover: (model: monaco.editor.ITextModel, position: monaco.Position) =>
                provideHover(schemaService, model, position)
        });
        this.toDispose.push(this.hoverProvider);
    }

    protected registerColorProvider(schemaService: LVGLSchemaService): void {
        const tagRe: RegExp = /<([\w:-]+)\b[^>]*>/g;
        const attrRe: RegExp = /(\b[\w:-]+)\s*=\s*(["'])(.*?)\2/g;
        const constRefRe: RegExp = /(["'])#([A-Za-z0-9_]+)\1/g;
        const hexRe: RegExp = /^(#|0x)([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

        const toColor = (hex: string): monaco.languages.IColor | null => {
            const hexMatch: RegExpMatchArray | null = hex.match(hexRe);
            if (!hexMatch) return null;

            let normalizedHex: string = hexMatch[2];
            if (normalizedHex.length === 3) {
                normalizedHex = normalizedHex
                    .split('')
                    .map((char) => char + char)
                    .join('');
            }

            return {
                red: parseInt(normalizedHex.slice(0, 2), 16) / 255,
                green: parseInt(normalizedHex.slice(2, 4), 16) / 255,
                blue: parseInt(normalizedHex.slice(4, 6), 16) / 255,
                alpha: 1
            };
        };

        this.colorProvider = monaco.languages.registerColorProvider(XML_LANGUAGE_ID, {
            provideDocumentColors: (
                model: monaco.editor.ITextModel
            ): monaco.languages.ProviderResult<monaco.languages.IColorInformation[]> => {
                if (model.getLanguageId() !== XML_LANGUAGE_ID) return [];

                const text: string = model.getValue();
                const colorInfos: monaco.languages.IColorInformation[] = [];

                for (let tagMatch: RegExpExecArray | null; (tagMatch = tagRe.exec(text));) {
                    const tagName: string = tagMatch[1];
                    const tagStart: number = tagMatch.index;
                    const tagText: string = tagMatch[0];

                    attrRe.lastIndex = 0;
                    for (let attributeMatch: RegExpExecArray | null; (attributeMatch = attrRe.exec(tagText));) {
                        const attributeName: string = attributeMatch[1];
                        const quoteChar: string = attributeMatch[2];
                        const rawValue: string = attributeMatch[3];
                        const isColorAttribute: boolean =
                            (tagName === 'color' && attributeName === 'value') ||
                            /_color$/.test(attributeName) ||
                            attributeName === 'color';
                        if (!isColorAttribute) continue;

                        const color: monaco.languages.IColor | null = toColor(rawValue);
                        if (!color) continue;

                        const openQuoteOffsetInAttribute: number = attributeMatch[0].indexOf(quoteChar);
                        const valueStartOffset: number =
                            tagStart + attributeMatch.index + openQuoteOffsetInAttribute + 1;
                        const valueEndOffset: number = valueStartOffset + rawValue.length;
                        const start: monaco.Position = model.getPositionAt(valueStartOffset);
                        const end: monaco.Position = model.getPositionAt(valueEndOffset);

                        colorInfos.push({
                            color,
                            range: new monaco.Range(start.lineNumber, start.column, end.lineNumber, end.column)
                        });
                    }
                }

                for (let constMatch: RegExpExecArray | null; (constMatch = constRefRe.exec(text));) {
                    const constKey: string = constMatch[2];
                    const resolvedValue: string | undefined = schemaService.getConstDefinition(constKey)?.value;
                    if (!resolvedValue) continue;

                    const color: monaco.languages.IColor | null = toColor(resolvedValue);
                    if (!color) continue;

                    const hashIndexInMatch: number = constMatch[0].indexOf('#');
                    const startOffset: number = constMatch.index + hashIndexInMatch;
                    const endOffset: number = startOffset + 1 + constKey.length;
                    const start: monaco.Position = model.getPositionAt(startOffset);
                    const end: monaco.Position = model.getPositionAt(endOffset);

                    colorInfos.push({
                        color,
                        range: new monaco.Range(start.lineNumber, start.column, end.lineNumber, end.column)
                    });
                }

                return colorInfos;
            },

            provideColorPresentations: (
                _model: monaco.editor.ITextModel,
                colorInfo: monaco.languages.IColorInformation
            ): monaco.languages.ProviderResult<monaco.languages.IColorPresentation[]> => {
                const red: number = Math.round(colorInfo.color.red * 255);
                const green: number = Math.round(colorInfo.color.green * 255);
                const blue: number = Math.round(colorInfo.color.blue * 255);
                const hex: string = `#${[red, green, blue].map((value) => value.toString(16).padStart(2, '0')).join('')}`;

                return [{ label: hex }, { label: `rgb(${red}, ${green}, ${blue})` }];
            }
        });
        this.toDispose.push(this.colorProvider);
    }

    protected registerDefinitionProvider(schemaService: LVGLSchemaService): void {
        this.definitionProvider = monaco.languages.registerDefinitionProvider(XML_LANGUAGE_ID, {
            provideDefinition: (
                model: monaco.editor.ITextModel,
                position: monaco.Position
            ): monaco.languages.ProviderResult<monaco.languages.Location | monaco.languages.Location[]> =>
                this.provideSchemaDefinition(schemaService, model, position)
        });
        this.toDispose.push(this.definitionProvider);
    }

    protected provideSchemaDefinition(
        schemaService: LVGLSchemaService,
        model: monaco.editor.ITextModel,
        position: monaco.Position
    ): monaco.languages.Location | undefined {
        const lineContent: string = model.getLineContent(position.lineNumber);
        const tagName: string | undefined = this.getElementTagAtPosition(lineContent, position.column);
        if (tagName) {
            const elementDefinition: ElementSchema | undefined = schemaService.getElement(tagName);
            return this.createSchemaLocation(elementDefinition?.filePath);
        }

        const word: monaco.editor.IWordAtPosition | null = model.getWordAtPosition(position);
        if (!word) return undefined;

        const isConstReference: boolean = word.startColumn > 1 && lineContent[word.startColumn - 2] === '#';
        if (isConstReference) {
            const constDefinition: ConstDefinitionData | undefined = schemaService.getConstDefinition(word.word);
            return this.createSchemaLocation(constDefinition?.filePath);
        }

        return undefined;
    }

    protected getElementTagAtPosition(lineContent: string, column: number): string | undefined {
        const cursorOffset: number = column - 1;
        const tagMatches: IterableIterator<RegExpMatchArray> = lineContent.matchAll(/<\s*\/?\s*([A-Za-z0-9_:-]+)/g);

        for (const tagMatch of tagMatches) {
            if (tagMatch.index === undefined) continue;

            const tagName: string = tagMatch[1];
            const tagNameOffset: number = tagMatch.index + tagMatch[0].lastIndexOf(tagName);
            const tagNameEndOffset: number = tagNameOffset + tagName.length;
            if (cursorOffset >= tagNameOffset && cursorOffset < tagNameEndOffset) return tagName;
        }

        return undefined;
    }

    protected createSchemaLocation(filePath: string | undefined): monaco.languages.Location | undefined {
        if (!filePath) return undefined;
        return {
            uri: monaco.Uri.file(filePath),
            range: new monaco.Range(1, 1, 1, 1)
        };
    }

    protected transformCompletions(completions: CoreCompletionList): monaco.languages.CompletionList {
        const completionKindLookup: Record<CoreCompletionItemKind, monaco.languages.CompletionItemKind> = {
            class: monaco.languages.CompletionItemKind.Class,
            enumMember: monaco.languages.CompletionItemKind.EnumMember,
            property: monaco.languages.CompletionItemKind.Property,
            color: monaco.languages.CompletionItemKind.Color,
            value: monaco.languages.CompletionItemKind.Value
        };

        const insertTextRulesLookup: Record<
            CoreCompletionItemInsertTextRule,
            monaco.languages.CompletionItemInsertTextRule
        > = {
            none: monaco.languages.CompletionItemInsertTextRule.None,
            keepWhitespace: monaco.languages.CompletionItemInsertTextRule.KeepWhitespace,
            insertAsSnippet: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet
        };

        return {
            ...completions,
            suggestions: completions.suggestions.map(
                (completion: CoreCompletionItem): monaco.languages.CompletionItem => ({
                    ...completion,
                    kind: completionKindLookup[completion.kind],
                    insertTextRules: completion.insertTextRules
                        ? insertTextRulesLookup[completion.insertTextRules]
                        : undefined
                })
            )
        };
    }
}
